import {
  AdminContentTier,
  AdminEdmArtworkDetail,
  AdminEdmArtworkListResponse,
  AdminEdmArtworkMediaValidationResult,
  AdminEdmBlockedRecord,
  AdminEdmContextEntity,
  AdminEdmContextRepresentation,
  AdminEdmContextSuggestion,
  AdminEdmDatasetDetail,
  AdminEdmDatasetListResponse,
  AdminEdmDatasetMediaValidationResult,
  AdminEdmDatasetSummary,
  AdminEdmHandoffPackage,
  AdminEdmParadata,
  AdminEdmPublicAvailability,
  AdminEdmPublicLinks,
  AdminEdmReadinessExplanation,
  AdminEdmSourceLinks,
  AdminEdmTechnicalMetadata,
  AdminEdmTransitionEvent,
  AdminMetadataTier,
  AdminRecordState,
} from "../../../types/adminEdm";
import { normalizeContextEntityForForm } from "./edm-authoring.shared";

const CONTEXT_TYPES = new Set(["Agent", "Place", "TimeSpan", "Concept"]);
const PROVIDED_CHO_LINKS = new Set(["creator", "hasMet"]);
const CONTEXT_SUGGESTION_STATUSES = new Set(["pending", "dismissed"]);
const RECORD_STATES = new Set(["draft", "ready", "published", "withdrawn"]);
const METADATA_TIERS = new Set<AdminMetadataTier>([0, "A", "B", "C"]);
const CONTENT_TIERS = new Set<AdminContentTier>([0, 1, 2, 3, 4]);
const PUBLIC_AVAILABILITY_REASONS = new Set([
  "live",
  "not_ready",
  "not_in_publication_state",
  "missing_dataset",
  "missing_profile",
  "missing_preview",
  "missing_direct_model",
]);
const PARADATA_CAPTURE_METHODS = new Set([
  "",
  "photogrammetry",
  "structured-light-scan",
  "laser-scan",
  "manual-3d-modelling",
  "bim",
  "other",
]);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};

const toNullableString = (value: unknown): string | null => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed || null;
};

const toStringValue = (value: unknown): string =>
  typeof value === "string" ? value : value == null ? "" : String(value);

const toStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
      .map((entry) => toNullableString(entry))
      .filter((entry): entry is string => Boolean(entry))
    : [];

const toNormalizedLanguageCodes = (value: unknown): string[] => {
  const seen = new Set<string>();
  return toStringList(value).reduce<string[]>((acc, entry) => {
    const normalized = entry.toLowerCase();
    if (!seen.has(normalized)) {
      seen.add(normalized);
      acc.push(normalized);
    }
    return acc;
  }, []);
};

const toCount = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const toNullableFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNullableIsoString = (value: unknown): string | null => {
  const normalized = toNullableString(value);
  if (!normalized) return null;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

export const getSafeHttpUrl = (value: unknown): string | null => {
  const normalized = toNullableString(value);
  if (!normalized) return null;

  try {
    const parsed = new URL(normalized);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? normalized : null;
  } catch (_error) {
    return null;
  }
};

const getSafeAdminPath = (value: unknown, fallback: string): string => {
  const normalized = toNullableString(value);
  return normalized && /^\/admin(?:\/|$)/.test(normalized) ? normalized : fallback;
};

const normalizeCountMap = (value: unknown): Record<string, number> =>
  Object.entries(asRecord(value)).reduce((acc, [key, rawCount]) => {
    const normalizedKey = toNullableString(key);
    if (!normalizedKey) {
      return acc;
    }
    acc[normalizedKey] = toCount(rawCount);
    return acc;
  }, {} as Record<string, number>);

const normalizeContextEntity = (value: unknown): AdminEdmContextEntity | null => {
  const context = asRecord(value);
  const type = toNullableString(context.type);
  if (!type || !CONTEXT_TYPES.has(type)) {
    return null;
  }

  const normalized = normalizeContextEntityForForm({
    ...context,
    type,
    mode: toNullableString(context.mode) === "reference" ? "reference" : "described",
    providedChoLink: type === "Agent" && PROVIDED_CHO_LINKS.has(String(context.providedChoLink || ""))
      ? context.providedChoLink as AdminEdmContextEntity["providedChoLink"]
      : null,
  } as AdminEdmContextEntity);

  return normalized;
};

export const normalizeAdminEdmContextSuggestions = (value: unknown): AdminEdmContextSuggestion[] =>
  Array.isArray(value)
    ? value
      .map((entry): AdminEdmContextSuggestion | null => {
        const suggestion = asRecord(entry);
        const id = toNullableString(suggestion.id);
        const type = toNullableString(suggestion.type);
        if (!id || !type || !CONTEXT_TYPES.has(type)) {
          return null;
        }

        const status = toNullableString(suggestion.status);
        return {
          id,
          type: type as AdminEdmContextSuggestion["type"],
          prefLabel: toNullableString(suggestion.prefLabel),
          lang: toNullableString(suggestion.lang),
          referenceUri: getSafeHttpUrl(suggestion.referenceUri),
          note: toNullableString(suggestion.note),
          begin: toNullableString(suggestion.begin),
          end: toNullableString(suggestion.end),
          sourceProvider: toNullableString(suggestion.sourceProvider),
          sourceKind: toNullableString(suggestion.sourceKind),
          status: status && CONTEXT_SUGGESTION_STATUSES.has(status) ? status as AdminEdmContextSuggestion["status"] : "pending",
        };
      })
      .filter((entry): entry is AdminEdmContextSuggestion => Boolean(entry))
    : [];

const emptyParadata = (): AdminEdmParadata => ({
  captureMethod: "",
  captureEquipment: [],
  captureSoftware: [],
  captureDate: "",
  operator: "",
  organisation: "",
  processingSteps: [],
  sourceAssetsSummary: "",
  qualityStatement: "",
  paradataUrl: "",
  paradataConformsTo: "",
  rightsNote: "",
});

const emptyTechnicalMetadata = (): AdminEdmTechnicalMetadata => ({
  vertexCount: null,
  polygonCount: null,
  pointCount: null,
  digitalSourceTypeUri: null,
  digitalSourceTypeOverrideUri: null,
  effectiveDigitalSourceTypeUri: null,
});

const normalizeAdminEdmTechnicalMetadata = (value: unknown): AdminEdmTechnicalMetadata => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return emptyTechnicalMetadata();
  }

  const technicalMetadata = asRecord(value);
  return {
    vertexCount: toNullableFiniteNumber(technicalMetadata.vertexCount),
    polygonCount: toNullableFiniteNumber(technicalMetadata.polygonCount),
    pointCount: toNullableFiniteNumber(technicalMetadata.pointCount),
    digitalSourceTypeUri: getSafeHttpUrl(technicalMetadata.digitalSourceTypeUri),
    digitalSourceTypeOverrideUri: getSafeHttpUrl(technicalMetadata.digitalSourceTypeOverrideUri),
    effectiveDigitalSourceTypeUri: getSafeHttpUrl(technicalMetadata.effectiveDigitalSourceTypeUri),
  };
};

export const normalizeAdminEdmParadata = (value: unknown): AdminEdmParadata => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return emptyParadata();
  }

  const paradata = asRecord(value);
  const captureMethod = toStringValue(paradata.captureMethod);

  return {
    captureMethod: PARADATA_CAPTURE_METHODS.has(captureMethod)
      ? captureMethod as AdminEdmParadata["captureMethod"]
      : "",
    captureEquipment: toStringList(paradata.captureEquipment),
    captureSoftware: toStringList(paradata.captureSoftware),
    captureDate: toStringValue(paradata.captureDate),
    operator: toStringValue(paradata.operator),
    organisation: toStringValue(paradata.organisation),
    processingSteps: toStringList(paradata.processingSteps),
    sourceAssetsSummary: toStringValue(paradata.sourceAssetsSummary),
    qualityStatement: toStringValue(paradata.qualityStatement),
    paradataUrl: toStringValue(paradata.paradataUrl),
    paradataConformsTo: toStringValue(paradata.paradataConformsTo),
    rightsNote: toStringValue(paradata.rightsNote),
  };
};

const normalizeAdminEdmPublicLinks = (value: unknown): AdminEdmPublicLinks => {
  const links = asRecord(value);
  return {
    publicEmbedUrl: getSafeHttpUrl(links.publicEmbedUrl),
    edmLandingUrl: getSafeHttpUrl(links.edmLandingUrl),
    rdfRecordUrl: getSafeHttpUrl(links.rdfRecordUrl),
    oaiGetRecordUrl: getSafeHttpUrl(links.oaiGetRecordUrl),
    oembedJsonUrl: getSafeHttpUrl(links.oembedJsonUrl),
    oembedXmlUrl: getSafeHttpUrl(links.oembedXmlUrl),
    previewWebResourceUrl: getSafeHttpUrl(links.previewWebResourceUrl),
    modelWebResourceUrl: getSafeHttpUrl(links.modelWebResourceUrl),
    paradataResourceUrl: getSafeHttpUrl(links.paradataResourceUrl),
  };
};

const normalizeAdminEdmDatasetPublicLinks = (value: unknown) => {
  const links = asRecord(value);
  return {
    oaiIdentifyUrl: getSafeHttpUrl(links.oaiIdentifyUrl),
    oaiListSetsUrl: getSafeHttpUrl(links.oaiListSetsUrl),
    oaiListIdentifiersUrl: getSafeHttpUrl(links.oaiListIdentifiersUrl),
    datasetRdfUrl: getSafeHttpUrl(links.datasetRdfUrl),
    oaiListRecordsUrl: getSafeHttpUrl(links.oaiListRecordsUrl),
    policyUrl: getSafeHttpUrl(links.policyUrl),
    handoffPackageUrl: getSafeHttpUrl(links.handoffPackageUrl),
  };
};

const normalizeAdminEdmSourceLinks = (value: unknown): AdminEdmSourceLinks => {
  const links = asRecord(value);
  return {
    previewSourceUrl: getSafeHttpUrl(links.previewSourceUrl),
    modelSourceUrl: getSafeHttpUrl(links.modelSourceUrl),
  };
};

const normalizeReadinessExplanation = (value: unknown): AdminEdmReadinessExplanation => {
  const explanation = asRecord(value);
  return {
    baselineBlockers: toStringList(explanation.baselineBlockers),
    currentTargetBlockers: toStringList(explanation.currentTargetBlockers),
    higherTierGaps: toStringList(explanation.higherTierGaps),
    nextTierHint: toNullableString(explanation.nextTierHint),
  };
};

const normalizeReadinessSnapshot = (value: unknown): AdminEdmArtworkDetail["readiness"] => {
  const readiness = asRecord(value);
  const metadataTierCurrent = readiness.metadataTierCurrent;
  const metadataTierTarget = readiness.metadataTierTarget;
  const contentTierCurrent = readiness.contentTierCurrent;
  const contentTierTarget = readiness.contentTierTarget;
  const dimensions = asRecord(readiness.metadataTierDimensions);

  return {
    edmCompatibilityStatus: readiness.edmCompatibilityStatus === "fail" ? "fail" : "pass",
    metadataTierCurrent: METADATA_TIERS.has(metadataTierCurrent as AdminMetadataTier) ? metadataTierCurrent as AdminMetadataTier : 0,
    metadataTierDimensions: {
      language: METADATA_TIERS.has(dimensions.language as AdminMetadataTier) ? dimensions.language as AdminMetadataTier : 0,
      enablingElements: METADATA_TIERS.has(dimensions.enablingElements as AdminMetadataTier) ? dimensions.enablingElements as AdminMetadataTier : 0,
      contextualClasses: METADATA_TIERS.has(dimensions.contextualClasses as AdminMetadataTier) ? dimensions.contextualClasses as AdminMetadataTier : 0,
    },
    metadataTierTarget: METADATA_TIERS.has(metadataTierTarget as AdminMetadataTier) ? metadataTierTarget as AdminMetadataTier : 0,
    contentTierCurrent: CONTENT_TIERS.has(contentTierCurrent as AdminContentTier) ? contentTierCurrent as AdminContentTier : 0,
    contentTierTarget: CONTENT_TIERS.has(contentTierTarget as AdminContentTier) ? contentTierTarget as AdminContentTier : 0,
    targetMet: Boolean(readiness.targetMet),
    checkedAt: toNullableIsoString(readiness.checkedAt) || new Date(0).toISOString(),
    validator: toStringValue(readiness.validator),
    policyVersion: toStringValue(readiness.policyVersion),
    blockers: {
      baseline: toStringList(asRecord(readiness.blockers).baseline),
      metadataTier: toStringList(asRecord(readiness.blockers).metadataTier),
      contentTier: toStringList(asRecord(readiness.blockers).contentTier),
    },
    warnings: toStringList(readiness.warnings),
    viewerSupport: {
      approved: Boolean(asRecord(readiness.viewerSupport).approved),
      registryId: (() => {
        const registryId = toNullableString(asRecord(readiness.viewerSupport).registryId);
        return registryId === "sketchfab" || registryId === "weave" || registryId === "artetra" ? registryId : null;
      })(),
      checkedUrl: getSafeHttpUrl(asRecord(readiness.viewerSupport).checkedUrl),
    },
  };
};

const normalizePublicAvailability = (value: unknown): AdminEdmPublicAvailability => {
  const availability = asRecord(value);
  const reason = toNullableString(availability.reason);
  return {
    exportableNow: Boolean(availability.exportableNow),
    reason: reason && PUBLIC_AVAILABILITY_REASONS.has(reason)
      ? reason as AdminEdmPublicAvailability["reason"]
      : "not_ready",
    allowedPublicationStates: toStringList(availability.allowedPublicationStates),
  };
};

const normalizeExportedContexts = (value: unknown) => {
  const exported = asRecord(value);
  const representations = Array.isArray(exported.representations)
    ? exported.representations
      .map((entry): AdminEdmContextRepresentation | null => {
        const representation = asRecord(entry);
        const type = toNullableString(representation.type);
        if (!type || !CONTEXT_TYPES.has(type)) {
          return null;
        }
        const status = toNullableString(representation.status);
        return {
          index: toCount(representation.index),
          type: type as AdminEdmContextRepresentation["type"],
          mode: toNullableString(representation.mode) === "reference" ? "reference" : "described",
          status: status === "represented" || status === "missing-fields" ? status : "link-only",
          label: toNullableString(representation.label),
          referenceUri: getSafeHttpUrl(representation.referenceUri),
          validationStatus: (() => {
            const validationStatus = toNullableString(representation.validationStatus);
            return validationStatus === "supported_canonical"
              || validationStatus === "supported_noncanonical"
              || validationStatus === "unsupported_vocabulary"
              || validationStatus === "missing_reference_uri"
              ? validationStatus
              : null;
          })(),
          supportedVocabularyId: toNullableString(representation.supportedVocabularyId),
        };
      })
      .filter((entry): entry is AdminEdmContextRepresentation => Boolean(entry))
    : [];

  return {
    presentTypes: toStringList(exported.presentTypes).filter((type): type is AdminEdmContextRepresentation["type"] => CONTEXT_TYPES.has(type)),
    agents: toStringList(exported.agents),
    places: toStringList(exported.places),
    timeSpans: toStringList(exported.timeSpans),
    concepts: toStringList(exported.concepts),
    representations,
  };
};

const normalizeTransitions = (value: unknown): AdminEdmTransitionEvent[] =>
  Array.isArray(value)
    ? value
      .map((entry): AdminEdmTransitionEvent | null => {
        const transition = asRecord(entry);
        const fromState = toNullableString(transition.fromState);
        const toState = toNullableString(transition.toState);
        if (!fromState || !toState || !RECORD_STATES.has(fromState) || !RECORD_STATES.has(toState)) {
          return null;
        }
        return {
          fromState: fromState as AdminRecordState,
          toState: toState as AdminRecordState,
          profileId: toNullableString(transition.profileId),
          actorUserId: toNullableString(transition.actorUserId),
          actorEmail: toNullableString(transition.actorEmail),
          requestId: toNullableString(transition.requestId),
          reason: toNullableString(transition.reason),
          timestamp: toNullableIsoString(transition.timestamp),
        };
      })
      .filter((entry): entry is AdminEdmTransitionEvent => Boolean(entry))
    : [];

const normalizeValidationIssue = (value: unknown) => {
  const issue = asRecord(value);
  const code = toNullableString(issue.code);
  const message = toNullableString(issue.message);
  if (!code || !message) {
    return null;
  }

  return {
    level: toNullableString(issue.level) === "warning" ? "warning" : "error",
    code,
    message,
    url: getSafeHttpUrl(issue.url),
    finalUrl: getSafeHttpUrl(issue.finalUrl),
    statusCode: issue.statusCode == null ? null : toCount(issue.statusCode),
    contentType: toNullableString(issue.contentType),
    redirectCount: toCount(issue.redirectCount),
  };
};

export const normalizeAdminEdmArtworkMediaValidation = (value: unknown): AdminEdmArtworkMediaValidationResult | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const validation = asRecord(value);
  return {
    artworkId: toStringValue(validation.artworkId),
    datasetKey: toNullableString(validation.datasetKey),
    effectiveProfileId: toNullableString(validation.effectiveProfileId),
    checkedAt: toNullableIsoString(validation.checkedAt) || new Date(0).toISOString(),
    passed: Boolean(validation.passed),
    errorCount: toCount(validation.errorCount),
    warningCount: toCount(validation.warningCount),
    issues: Array.isArray(validation.issues)
      ? validation.issues
        .map((issue) => normalizeValidationIssue(issue))
        .filter((issue): issue is NonNullable<AdminEdmArtworkMediaValidationResult["issues"][number]> => Boolean(issue))
      : [],
  };
};

export const normalizeAdminEdmDatasetMediaValidation = (value: unknown): AdminEdmDatasetMediaValidationResult | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const validation = asRecord(value);
  return {
    datasetKey: toStringValue(validation.datasetKey),
    profileId: toNullableString(validation.profileId),
    checkedAt: toNullableIsoString(validation.checkedAt) || new Date(0).toISOString(),
    scanned: toCount(validation.scanned),
    passed: toCount(validation.passed),
    failed: toCount(validation.failed),
    warningCount: toCount(validation.warningCount),
    records: Array.isArray(validation.records)
      ? validation.records
        .map((entry) => {
          const record = asRecord(entry);
          const artworkId = toNullableString(record.artworkId);
          if (!artworkId) {
            return null;
          }
          const recordState = toNullableString(record.recordState);
          return {
            artworkId,
            title: toNullableString(record.title),
            recordState: recordState && RECORD_STATES.has(recordState) ? recordState as AdminRecordState : "draft",
            targetMet: Boolean(record.targetMet),
            passed: Boolean(record.passed),
            errorCount: toCount(record.errorCount),
            warningCount: toCount(record.warningCount),
            firstIssue: normalizeValidationIssue(record.firstIssue),
            issues: Array.isArray(record.issues)
              ? record.issues
                .map((issue) => normalizeValidationIssue(issue))
                .filter((issue): issue is NonNullable<AdminEdmDatasetMediaValidationResult["records"][number]["issues"][number]> => Boolean(issue))
              : [],
          };
        })
        .filter((entry): entry is AdminEdmDatasetMediaValidationResult["records"][number] => Boolean(entry))
      : [],
  };
};

export const normalizeAdminEdmDatasetSummary = (value: unknown): AdminEdmDatasetSummary | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const summary = asRecord(value);
  return {
    datasetKey: toStringValue(summary.datasetKey),
    profileId: toNullableString(summary.profileId),
    policyVersion: toStringValue(summary.policyVersion),
    edmValidCount: toCount(summary.edmValidCount),
    publishableA1: toCount(summary.publishableA1),
    publishableB2: toCount(summary.publishableB2),
    publishableC3: toCount(summary.publishableC3),
    publishableC4: toCount(summary.publishableC4),
    tierCapableA1: toCount(summary.tierCapableA1),
    tierCapableB2: toCount(summary.tierCapableB2),
    tierCapableC3: toCount(summary.tierCapableC3),
    tierCapableC4: toCount(summary.tierCapableC4),
    blocked: {
      baseline: normalizeCountMap(asRecord(summary.blocked).baseline),
      metadataTier: normalizeCountMap(asRecord(summary.blocked).metadataTier),
      contentTier: normalizeCountMap(asRecord(summary.blocked).contentTier),
    },
  };
};

export const normalizeAdminEdmBlockedRecords = (value: unknown): AdminEdmBlockedRecord[] =>
  Array.isArray(value)
    ? value
      .map((entry): AdminEdmBlockedRecord | null => {
        const record = asRecord(entry);
        const artworkId = toNullableString(record.artworkId);
        if (!artworkId) {
          return null;
        }
        const recordState = toNullableString(record.recordState);
        const metadataTier = record.metadataTierCurrent;
        const contentTier = record.contentTierCurrent;
        return {
          artworkId,
          title: toNullableString(record.title),
          datasetKey: toStringValue(record.datasetKey),
          profileId: toNullableString(record.profileId),
          recordState: recordState && RECORD_STATES.has(recordState) ? recordState as AdminRecordState : "draft",
          blockers: {
            baseline: toStringList(asRecord(record.blockers).baseline),
            metadataTier: toStringList(asRecord(record.blockers).metadataTier),
            contentTier: toStringList(asRecord(record.blockers).contentTier),
          },
          metadataTierCurrent: METADATA_TIERS.has(metadataTier as AdminMetadataTier) ? metadataTier as AdminMetadataTier : 0,
          contentTierCurrent: CONTENT_TIERS.has(contentTier as AdminContentTier) ? contentTier as AdminContentTier : 0,
          checkedAt: toNullableIsoString(record.checkedAt) || new Date(0).toISOString(),
        };
      })
      .filter((entry): entry is AdminEdmBlockedRecord => Boolean(entry))
    : [];

export const normalizeAdminEdmHandoffPackage = (value: unknown): AdminEdmHandoffPackage | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const handoff = asRecord(value);
  return {
    datasetKey: toStringValue(handoff.datasetKey),
    effectiveDeliveryProfile: {
      profileId: toStringValue(asRecord(handoff.effectiveDeliveryProfile).profileId),
      type: toStringValue(asRecord(handoff.effectiveDeliveryProfile).type),
      provider: toNullableString(asRecord(handoff.effectiveDeliveryProfile).provider),
      intermediateProvider: toNullableString(asRecord(handoff.effectiveDeliveryProfile).intermediateProvider),
      minimumMetadataTier: METADATA_TIERS.has(asRecord(handoff.effectiveDeliveryProfile).minimumMetadataTier as AdminMetadataTier)
        ? asRecord(handoff.effectiveDeliveryProfile).minimumMetadataTier as AdminMetadataTier
        : 0,
      minimumContentTier: CONTENT_TIERS.has(asRecord(handoff.effectiveDeliveryProfile).minimumContentTier as AdminContentTier)
        ? asRecord(handoff.effectiveDeliveryProfile).minimumContentTier as AdminContentTier
        : 0,
    },
    oaiIdentifyUrl: getSafeHttpUrl(handoff.oaiIdentifyUrl),
    oaiListSetsUrl: getSafeHttpUrl(handoff.oaiListSetsUrl),
    oaiListIdentifiersUrl: getSafeHttpUrl(handoff.oaiListIdentifiersUrl),
    oaiListRecordsUrl: getSafeHttpUrl(handoff.oaiListRecordsUrl),
    datasetRdfUrl: getSafeHttpUrl(handoff.datasetRdfUrl),
    exportableRecords: Array.isArray(handoff.exportableRecords)
      ? handoff.exportableRecords
        .map((entry): AdminEdmHandoffPackage["exportableRecords"][number] | null => {
          const record = asRecord(entry);
          const artworkId = toNullableString(record.artworkId);
          if (!artworkId) {
            return null;
          }
          return {
            artworkId,
            title: toNullableString(record.title),
            rdfRecordUrl: getSafeHttpUrl(record.rdfRecordUrl),
            landingPageUrl: getSafeHttpUrl(record.landingPageUrl),
          };
        })
        .filter((entry): entry is AdminEdmHandoffPackage["exportableRecords"][number] => Boolean(entry))
      : [],
    exportableRecordCount: toCount(handoff.exportableRecordCount),
    publishableCounts: {
      publishableA1: toCount(asRecord(handoff.publishableCounts).publishableA1),
      publishableB2: toCount(asRecord(handoff.publishableCounts).publishableB2),
      publishableC3: toCount(asRecord(handoff.publishableCounts).publishableC3),
      publishableC4: toCount(asRecord(handoff.publishableCounts).publishableC4),
    },
    blockedSummary: {
      baseline: normalizeCountMap(asRecord(handoff.blockedSummary).baseline),
      metadataTier: normalizeCountMap(asRecord(handoff.blockedSummary).metadataTier),
      contentTier: normalizeCountMap(asRecord(handoff.blockedSummary).contentTier),
    },
    blockedRecordCount: toCount(handoff.blockedRecordCount),
    releaseTarget: toStringValue(handoff.releaseTarget),
    viewerStatus: toStringValue(handoff.viewerStatus),
    warnings: toStringList(handoff.warnings),
    generatedAt: toNullableIsoString(handoff.generatedAt) || new Date(0).toISOString(),
  };
};

export const normalizeAdminEdmArtworkDetail = (value: unknown): AdminEdmArtworkDetail | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const detail = value as AdminEdmArtworkDetail;
  const artwork = asRecord(detail.artwork);
  const source = asRecord(artwork.source);
  const media = asRecord(artwork.media);
  const edmProfile = asRecord(detail.edmProfile);
  const capabilities = asRecord(detail.capabilities);

  return {
    ...detail,
    capabilities: {
      canDelete: capabilities.canDelete !== false,
    },
    artwork: {
      ...detail.artwork,
      source: detail.artwork?.source
        ? {
            ...detail.artwork.source,
            sourceUrl: getSafeHttpUrl(source.sourceUrl),
            itemUrl: getSafeHttpUrl(source.itemUrl),
            thumbnailUrl: getSafeHttpUrl(source.thumbnailUrl),
            edmRightsUri: getSafeHttpUrl(source.edmRightsUri),
          }
        : null,
      media: {
        ...detail.artwork.media,
        dataUrl: getSafeHttpUrl(media.dataUrl),
        originalUrl: getSafeHttpUrl(media.originalUrl),
        thumbnailUrl: getSafeHttpUrl(media.thumbnailUrl),
      },
    },
    edmProfile: {
      ...detail.edmProfile,
      exportSourceLabelsAsSubjects: edmProfile.exportSourceLabelsAsSubjects !== false,
      publicDownloadEnabled: edmProfile.publicDownloadEnabled !== false,
      materials: Array.isArray(edmProfile.materials) ? edmProfile.materials : [],
      relatedTo: Array.isArray(edmProfile.relatedTo) ? edmProfile.relatedTo : [],
      languages: toNormalizedLanguageCodes(edmProfile.languages),
      technicalMetadata: normalizeAdminEdmTechnicalMetadata(edmProfile.technicalMetadata),
      contextEntities: Array.isArray(edmProfile.contextEntities)
        ? edmProfile.contextEntities
          .map((context) => normalizeContextEntity(context))
          .filter((context): context is AdminEdmContextEntity => Boolean(context))
        : [],
      contextSuggestions: normalizeAdminEdmContextSuggestions(edmProfile.contextSuggestions),
      paradata: edmProfile.paradata ? normalizeAdminEdmParadata(edmProfile.paradata) : null,
    },
    readiness: normalizeReadinessSnapshot(detail.readiness),
    readinessExplanation: normalizeReadinessExplanation(detail.readinessExplanation),
    publicAvailability: normalizePublicAvailability(detail.publicAvailability),
    livePublicLinks: normalizeAdminEdmPublicLinks(detail.livePublicLinks),
    plannedPublicLinks: normalizeAdminEdmPublicLinks(detail.plannedPublicLinks),
    sourceLinks: normalizeAdminEdmSourceLinks(detail.sourceLinks),
    editorLinks: {
      legacyArtworkEditorPath: getSafeAdminPath(asRecord(detail.editorLinks).legacyArtworkEditorPath, `/admin/artworks/edit/${detail.artwork.artworkId}`),
    },
    publicLinks: normalizeAdminEdmPublicLinks(detail.publicLinks),
    exportedContexts: normalizeExportedContexts(detail.exportedContexts),
    recentTransitions: normalizeTransitions(detail.recentTransitions),
  };
};

export const normalizeAdminEdmDatasetDetail = (value: unknown): AdminEdmDatasetDetail | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const detail = value as AdminEdmDatasetDetail;
  const capabilities = asRecord(detail.capabilities);

  return {
    ...detail,
    capabilities: {
      canEditMetadata: capabilities.canEditMetadata !== false,
      canEditInstitution: capabilities.canEditInstitution !== false,
      canEditDeliveryProfiles: capabilities.canEditDeliveryProfiles !== false,
      canGenerateHandoff: capabilities.canGenerateHandoff !== false,
      canToggleEnabled: capabilities.canToggleEnabled !== false,
      canDelete: capabilities.canDelete !== false,
    },
    deliveryProfiles: Array.isArray(detail.deliveryProfiles) ? detail.deliveryProfiles : [],
    publicLinks: normalizeAdminEdmDatasetPublicLinks(detail.publicLinks),
  };
};

export const normalizeAdminEdmArtworkListResponse = (value: unknown): AdminEdmArtworkListResponse => {
  const payload = asRecord(value);
  return {
    items: Array.isArray(payload.items) ? payload.items : [],
    total: toCount(payload.total),
    page: Math.max(1, toCount(payload.page) || 1),
    limit: Math.max(1, toCount(payload.limit) || 25),
  };
};

export const normalizeAdminEdmDatasetListResponse = (value: unknown): AdminEdmDatasetListResponse => {
  const payload = asRecord(value);
  return {
    items: Array.isArray(payload.items) ? payload.items : [],
    total: toCount(payload.total),
  };
};
